import React, { useState, useEffect } from 'react';
import {
  Paper, Typography, Box, CircularProgress, Alert, Chip,
  Tabs, Tab, Card, CardContent, Divider, Button,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  List, ListItem, ListItemIcon, ListItemText, IconButton,
  TextField, FormControl, InputLabel, Select, MenuItem
} from '@mui/material';
import {
  ArrowBack, Edit, Delete, Business, Phone, Email,
  LocationOn, Receipt, Assignment, Assessment, Notes,
  Info, ContactPhone, Place, AccountBalance, Gavel,
  ContactMail, Badge, CalendarToday, Save, Cancel
} from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router-dom';
import { clientesApi } from '../../services/api';
import { Cliente, ClienteUpdate, TIPOS_CLIENTE } from '../../types/expediente';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`cliente-tabpanel-${index}`}
      aria-labelledby={`cliente-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

const ClienteDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [cliente, setCliente] = useState<Cliente | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tabValue, setTabValue] = useState(0);
  
  // Estados para edición
  const [editMode, setEditMode] = useState(false);
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [originalData, setOriginalData] = useState<Cliente | null>(null);
  const [formData, setFormData] = useState<ClienteUpdate>({
    codigo: '',
    nombre: '',
    tipo: '',
    direccion: '',
    contacto: '',
    telefono: '',
    email: '',
    nif: ''
  });

  useEffect(() => {
    if (id) {
      loadCliente(id);
    }
  }, [id]);

  // Detectar cambios en el formulario
  useEffect(() => {
    if (originalData && editMode) {
      const hasChangesNow = 
        formData.codigo !== (originalData.codigo || '') ||
        formData.nombre !== originalData.nombre ||
        formData.tipo !== (originalData.tipo || '') ||
        formData.direccion !== (originalData.direccion || '') ||
        formData.contacto !== (originalData.contacto || '') ||
        formData.telefono !== (originalData.telefono || '') ||
        formData.email !== (originalData.email || '') ||
        formData.nif !== (originalData.nif || '');
      
      setHasChanges(hasChangesNow);
    }
  }, [formData, originalData, editMode]);

  // Advertir antes de salir con cambios sin guardar
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (hasChanges) {
        e.preventDefault();
        e.returnValue = '';
      }
    };

    if (hasChanges) {
      window.addEventListener('beforeunload', handleBeforeUnload);
      return () => window.removeEventListener('beforeunload', handleBeforeUnload);
    }
  }, [hasChanges]);

  const loadCliente = async (clienteId: string) => {
    try {
      setLoading(true);
      const data = await clientesApi.getCliente(clienteId);
      setCliente(data);
      setOriginalData(data);
      
      // Inicializar formulario con datos actuales
      setFormData({
        codigo: data.codigo || '',
        nombre: data.nombre || '',
        tipo: data.tipo || '',
        direccion: data.direccion || '',
        contacto: data.contacto || '',
        telefono: data.telefono || '',
        email: data.email || '',
        nif: data.nif || ''
      });
    } catch (err: any) {
      setError('Error cargando cliente');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  // Funciones de edición
  const handleEdit = () => {
    setEditMode(true);
  };

  const handleCancel = () => {
    if (hasChanges) {
      const confirmed = window.confirm('¿Estás seguro? Se perderán los cambios no guardados.');
      if (!confirmed) return;
    }
    
    // Restaurar datos originales
    if (originalData) {
      setFormData({
        codigo: originalData.codigo || '',
        nombre: originalData.nombre || '',
        tipo: originalData.tipo || '',
        direccion: originalData.direccion || '',
        contacto: originalData.contacto || '',
        telefono: originalData.telefono || '',
        email: originalData.email || '',
        nif: originalData.nif || ''
      });
    }
    
    setEditMode(false);
    setHasChanges(false);
  };

  const handleSave = async () => {
    if (!id || !hasChanges) return;

    try {
      setSaving(true);
      const updatedCliente = await clientesApi.updateCliente(id, formData);
      
      // Actualizar estados
      setCliente(updatedCliente);
      setOriginalData(updatedCliente);
      setEditMode(false);
      setHasChanges(false);
      
      // Mostrar mensaje de éxito (opcional)
      console.log('Cliente actualizado correctamente');
      
    } catch (err: any) {
      setError(err.message || 'Error actualizando cliente');
    } finally {
      setSaving(false);
    }
  };

  const handleInputChange = (field: keyof ClienteUpdate, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleNavigateBack = () => {
    if (hasChanges) {
      const confirmed = window.confirm('¿Estás seguro? Se perderán los cambios no guardados.');
      if (!confirmed) return;
    }
    navigate('/clientes');
  };

  const getTipoColor = (tipo: string) => {
    switch (tipo?.toLowerCase()) {
      case 'juzgado': return 'primary';
      case 'despacho': return 'secondary';
      case 'particular': return 'success';
      case 'empresa': return 'warning';
      case 'administración': return 'info';
      case 'penal': return 'error';
      default: return 'default';
    }
  };

  const formatDate = (dateString: string | null | undefined) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('es-ES');
  };

  const formatValue = (value: any) => {
    if (value === null || value === undefined || value === '') return '-';
    if (typeof value === 'boolean') return value ? 'Sí' : 'No';
    return String(value);
  };

  // Datos mock para expedientes relacionados
  const mockExpedientesRelacionados = [
    { id: 1, codigo: 'EXP-187', tipo: 'Tasación Judicial', estado: 'Activo', importe: 1500.00, fecha: '2025-01-15' },
    { id: 2, codigo: 'EXP-093', tipo: 'Peritaje Técnico', estado: 'Cobrado', importe: 2200.50, fecha: '2024-12-10' },
    { id: 3, codigo: 'EXP-216', tipo: 'Valoración', estado: 'En proceso', importe: 1800.00, fecha: '2025-01-08' }
  ];

  // Datos mock para estadísticas
  const mockEstadisticas = {
    totalExpedientes: 15,
    expedientesActivos: 3,
    totalFacturado: 28750.50,
    ultimaActividad: '2025-01-15'
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" mt={4}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !cliente) {
    return <Alert severity="error">{error || 'Cliente no encontrado'}</Alert>;
  }

  return (
    <Box>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box display="flex" alignItems="center" gap={2}>
          <Button
            startIcon={<ArrowBack />}
            onClick={handleNavigateBack}
            variant="outlined"
          >
            Volver
          </Button>
          <Typography variant="h4">
            Cliente: {cliente.codigo || cliente.id.slice(0, 8)}
          </Typography>
          <Chip
            label={cliente.tipo || 'Sin tipo'}
            color={getTipoColor(cliente.tipo || '') as any}
            size="medium"
          />
          {editMode && hasChanges && (
            <Chip 
              label="Cambios sin guardar" 
              color="warning" 
              size="small" 
            />
          )}
        </Box>
        <Box display="flex" gap={1}>
          {!editMode ? (
            <>
              <Button 
                startIcon={<Edit />} 
                variant="contained" 
                color="primary"
                onClick={handleEdit}
              >
                Editar
              </Button>
              <Button startIcon={<Delete />} variant="outlined" color="error">
                Eliminar
              </Button>
            </>
          ) : (
            <>
              <Button 
                startIcon={<Cancel />} 
                variant="outlined" 
                onClick={handleCancel}
                disabled={saving}
              >
                Cancelar
              </Button>
              <Button 
                startIcon={saving ? <CircularProgress size={16} /> : <Save />} 
                variant="contained" 
                color="success"
                onClick={handleSave}
                disabled={!hasChanges || saving}
              >
                {saving ? 'Guardando...' : 'Guardar'}
              </Button>
            </>
          )}
        </Box>
      </Box>

      {/* Información básica destacada */}
      <Paper sx={{ mb: 3, p: 2 }}>
        <Box sx={{ 
          display: 'grid', 
          gridTemplateColumns: { xs: '1fr', md: '1fr 1fr 1fr 1fr' }, 
          gap: 3 
        }}>
          <Box>
            <Typography variant="subtitle2" color="textSecondary">Nombre</Typography>
            {editMode ? (
              <TextField
                fullWidth
                value={formData.nombre}
                onChange={(e) => handleInputChange('nombre', e.target.value)}
                variant="standard"
                required
              />
            ) : (
              <Typography variant="h6">{cliente.nombre}</Typography>
            )}
          </Box>
          <Box>
            <Typography variant="subtitle2" color="textSecondary">Código</Typography>
            {editMode ? (
              <TextField
                fullWidth
                value={formData.codigo}
                onChange={(e) => handleInputChange('codigo', e.target.value)}
                variant="standard"
              />
            ) : (
              <Typography variant="h6">{cliente.codigo || '-'}</Typography>
            )}
          </Box>
          <Box>
            <Typography variant="subtitle2" color="textSecondary">Tipo</Typography>
            {editMode ? (
              <FormControl fullWidth variant="standard">
                <Select
                  value={formData.tipo}
                  onChange={(e) => handleInputChange('tipo', e.target.value)}
                >
                  <MenuItem value="">Sin especificar</MenuItem>
                  {TIPOS_CLIENTE.map(tipo => (
                    <MenuItem key={tipo} value={tipo}>{tipo}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            ) : (
              <Typography variant="h6">{cliente.tipo || '-'}</Typography>
            )}
          </Box>
          <Box>
            <Typography variant="subtitle2" color="textSecondary">NIF</Typography>
            {editMode ? (
              <TextField
                fullWidth
                value={formData.nif}
                onChange={(e) => handleInputChange('nif', e.target.value)}
                variant="standard"
              />
            ) : (
              <Typography variant="h6" fontFamily="monospace">
                {cliente.nif || '-'}
              </Typography>
            )}
          </Box>
        </Box>
      </Paper>

      {/* Pestañas */}
      <Paper>
        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          variant="scrollable"
          scrollButtons="auto"
          sx={{ borderBottom: 1, borderColor: 'divider' }}
        >
          <Tab icon={<Info />} label="Información General" />
          <Tab icon={<ContactPhone />} label="Contacto" />
          <Tab icon={<Place />} label="Ubicación" />
          <Tab icon={<Receipt />} label="Datos Fiscales" />
          <Tab icon={<Assessment />} label="Expedientes" />
          <Tab icon={<AccountBalance />} label="Estadísticas" />
        </Tabs>

        {/* Pestaña Información General */}
        <TabPanel value={tabValue} index={0}>
          <Box sx={{ 
            display: 'grid', 
            gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: '1fr 1fr 1fr' }, 
            gap: 3 
          }}>
            <Card>
              <CardContent>
                <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                  <Badge sx={{ mr: 1 }} />
                  CÓDIGO CLIENTE
                </Typography>
                {editMode ? (
                  <TextField
                    fullWidth
                    value={formData.codigo}
                    onChange={(e) => handleInputChange('codigo', e.target.value)}
                    variant="outlined"
                    size="small"
                  />
                ) : (
                  <Typography variant="h6" sx={{ fontFamily: 'monospace' }}>
                    {formatValue(cliente.codigo)}
                  </Typography>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardContent>
                <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                  <Business sx={{ mr: 1 }} />
                  NOMBRE COMPLETO
                </Typography>
                {editMode ? (
                  <TextField
                    fullWidth
                    value={formData.nombre}
                    onChange={(e) => handleInputChange('nombre', e.target.value)}
                    variant="outlined"
                    size="small"
                    required
                  />
                ) : (
                  <Typography variant="body1">
                    {formatValue(cliente.nombre)}
                  </Typography>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardContent>
                <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                  <Gavel sx={{ mr: 1 }} />
                  TIPO DE CLIENTE
                </Typography>
                {editMode ? (
                  <FormControl fullWidth size="small">
                    <Select
                      value={formData.tipo}
                      onChange={(e) => handleInputChange('tipo', e.target.value)}
                    >
                      <MenuItem value="">Sin especificar</MenuItem>
                      {TIPOS_CLIENTE.map(tipo => (
                        <MenuItem key={tipo} value={tipo}>{tipo}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                ) : (
                  <Chip
                    label={cliente.tipo || 'Sin especificar'}
                    color={getTipoColor(cliente.tipo || '') as any}
                    size="medium"
                  />
                )}
              </CardContent>
            </Card>

            <Card>
              <CardContent>
                <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                  <CalendarToday sx={{ mr: 1 }} />
                  FECHA DE REGISTRO
                </Typography>
                <Typography variant="body1">
                  {formatDate(cliente.created_at)}
                </Typography>
              </CardContent>
            </Card>

            <Card>
              <CardContent>
                <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                  <CalendarToday sx={{ mr: 1 }} />
                  ÚLTIMA ACTUALIZACIÓN
                </Typography>
                <Typography variant="body1">
                  {formatDate(cliente.updated_at)}
                </Typography>
              </CardContent>
            </Card>

            <Card>
              <CardContent>
                <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                  ID INTERNO
                </Typography>
                <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>
                  {cliente.id}
                </Typography>
              </CardContent>
            </Card>
          </Box>
        </TabPanel>

        {/* Pestaña Contacto */}
        <TabPanel value={tabValue} index={1}>
          <Box sx={{ 
            display: 'grid', 
            gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, 
            gap: 3 
          }}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  <ContactPhone sx={{ mr: 1 }} />
                  Información de Contacto
                </Typography>
                <Divider sx={{ mb: 2 }} />
                <Box display="flex" flexDirection="column" gap={2}>
                  <Box display="flex" alignItems="center" gap={1}>
                    <Phone color="action" />
                    <Box sx={{ flexGrow: 1 }}>
                      <Typography variant="body2" color="textSecondary">Teléfono:</Typography>
                      {editMode ? (
                        <TextField
                          fullWidth
                          value={formData.telefono}
                          onChange={(e) => handleInputChange('telefono', e.target.value)}
                          variant="standard"
                          size="small"
                        />
                      ) : (
                        <Typography variant="body1">
                          {cliente.telefono || 'No especificado'}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                  
                  <Box display="flex" alignItems="center" gap={1}>
                    <Email color="action" />
                    <Box sx={{ flexGrow: 1 }}>
                      <Typography variant="body2" color="textSecondary">Email:</Typography>
                      {editMode ? (
                        <TextField
                          fullWidth
                          value={formData.email}
                          onChange={(e) => handleInputChange('email', e.target.value)}
                          variant="standard"
                          size="small"
                          type="email"
                        />
                      ) : (
                        <Typography variant="body1">
                          {cliente.email || 'No especificado'}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                </Box>
              </CardContent>
            </Card>

            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  <ContactMail sx={{ mr: 1 }} />
                  Persona de Contacto
                </Typography>
                <Divider sx={{ mb: 2 }} />
                <Box>
                  <Typography variant="body2" color="textSecondary">Nombre del contacto:</Typography>
                  {editMode ? (
                    <TextField
                      fullWidth
                      value={formData.contacto}
                      onChange={(e) => handleInputChange('contacto', e.target.value)}
                      variant="outlined"
                      size="small"
                    />
                  ) : (
                    <Typography variant="h6">
                      {cliente.contacto || 'No especificado'}
                    </Typography>
                  )}
                </Box>
              </CardContent>
            </Card>
          </Box>
        </TabPanel>

        {/* Pestaña Ubicación */}
        <TabPanel value={tabValue} index={2}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                <LocationOn sx={{ mr: 1 }} />
                Dirección
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <Box>
                {editMode ? (
                  <TextField
                    fullWidth
                    value={formData.direccion}
                    onChange={(e) => handleInputChange('direccion', e.target.value)}
                    variant="outlined"
                    multiline
                    rows={4}
                    placeholder="Introduce la dirección..."
                  />
                ) : (
                  <Typography variant="body1" sx={{ minHeight: '100px', whiteSpace: 'pre-wrap' }}>
                    {cliente.direccion || 'Dirección no especificada'}
                  </Typography>
                )}
              </Box>
              <Box mt={2}>
                <Button variant="outlined" startIcon={<Edit />}>
                  Editar Dirección
                </Button>
              </Box>
            </CardContent>
          </Card>
        </TabPanel>

        {/* Pestaña Datos Fiscales */}
        <TabPanel value={tabValue} index={3}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                <Receipt sx={{ mr: 1 }} />
                Información Fiscal
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <Box sx={{ 
                display: 'grid', 
                gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, 
                gap: 3 
              }}>
                <Box>
                  <Typography variant="body2" color="textSecondary">NIF/CIF:</Typography>
                  {editMode ? (
                    <TextField
                      fullWidth
                      value={formData.nif}
                      onChange={(e) => handleInputChange('nif', e.target.value)}
                      variant="outlined"
                      size="small"
                    />
                  ) : (
                    <Typography variant="h6" fontFamily="monospace">
                      {cliente.nif || 'No especificado'}
                    </Typography>
                  )}
                </Box>
                <Box>
                  <Typography variant="body2" color="textSecondary">Tipo de entidad:</Typography>
                  {editMode ? (
                    <FormControl fullWidth size="small">
                      <Select
                        value={formData.tipo}
                        onChange={(e) => handleInputChange('tipo', e.target.value)}
                      >
                        <MenuItem value="">Sin especificar</MenuItem>
                        {TIPOS_CLIENTE.map(tipo => (
                          <MenuItem key={tipo} value={tipo}>{tipo}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  ) : (
                    <Typography variant="body1">
                      {cliente.tipo || 'No especificado'}
                    </Typography>
                  )}
                </Box>
              </Box>
            </CardContent>
          </Card>
        </TabPanel>

        {/* Pestaña Expedientes Relacionados */}
        <TabPanel value={tabValue} index={4}>
          <Box mb={3}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
              <Typography variant="h6">Expedientes Relacionados</Typography>
              <Button
                variant="contained"
                startIcon={<Assignment />}
                color="primary"
              >
                Nuevo Expediente
              </Button>
            </Box>
            
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Código</TableCell>
                    <TableCell>Tipo</TableCell>
                    <TableCell>Estado</TableCell>
                    <TableCell>Importe</TableCell>
                    <TableCell>Fecha</TableCell>
                    <TableCell>Acciones</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {mockExpedientesRelacionados.map((expediente) => (
                    <TableRow key={expediente.id} hover>
                      <TableCell>
                        <Typography variant="body2" fontWeight="bold">
                          {expediente.codigo}
                        </Typography>
                      </TableCell>
                      <TableCell>{expediente.tipo}</TableCell>
                      <TableCell>
                        <Chip 
                          label={expediente.estado} 
                          color={
                            expediente.estado === 'Activo' ? 'success' : 
                            expediente.estado === 'Cobrado' ? 'info' : 'warning'
                          } 
                          size="small" 
                        />
                      </TableCell>
                      <TableCell>{expediente.importe.toFixed(2)}€</TableCell>
                      <TableCell>{new Date(expediente.fecha).toLocaleDateString('es-ES')}</TableCell>
                      <TableCell>
                        <Button size="small" onClick={() => navigate(`/expedientes/${expediente.id}`)}>
                          Ver
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        </TabPanel>

        {/* Pestaña Estadísticas */}
        <TabPanel value={tabValue} index={5}>
          <Box sx={{ 
            display: 'grid', 
            gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, 
            gap: 3 
          }}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom color="primary">
                  📊 Resumen de Actividad
                </Typography>
                <Divider sx={{ mb: 2 }} />
                <Box display="flex" flexDirection="column" gap={2}>
                  <Box display="flex" justifyContent="space-between">
                    <Typography variant="body2" color="textSecondary">Total Expedientes:</Typography>
                    <Typography variant="h6" color="primary">
                      {mockEstadisticas.totalExpedientes}
                    </Typography>
                  </Box>
                  <Box display="flex" justifyContent="space-between">
                    <Typography variant="body2" color="textSecondary">Expedientes Activos:</Typography>
                    <Typography variant="body1">
                      {mockEstadisticas.expedientesActivos}
                    </Typography>
                  </Box>
                  <Divider />
                  <Box display="flex" justifyContent="space-between">
                    <Typography variant="body1" fontWeight="bold">Total Facturado:</Typography>
                    <Typography variant="h5" color="success.main">
                      {mockEstadisticas.totalFacturado.toFixed(2)}€
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>

            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom color="secondary">
                  🕐 Actividad Reciente
                </Typography>
                <Divider sx={{ mb: 2 }} />
                <Box>
                  <Typography variant="body2" color="textSecondary">Última actividad:</Typography>
                  <Typography variant="body1">
                    {new Date(mockEstadisticas.ultimaActividad).toLocaleDateString('es-ES')}
                  </Typography>
                  
                  <Box mt={2}>
                    <Button variant="outlined" size="small">
                      Ver Historial Completo
                    </Button>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Box>
        </TabPanel>
      </Paper>
    </Box>
  );
};

export default ClienteDetail;